import React, { useState, useEffect, useCallback } from 'react';

const FileOperationModal = ({
  isOpen,
  onClose,
  file,
  operation,
  onConfirm,
}) => {
  const [selectedModule, setSelectedModule] = useState("");

  // 面包屑式选择状态
  const [level1Folders, setLevel1Folders] = useState([]);
  const [level2Folders, setLevel2Folders] = useState([]);
  const [level3Folders, setLevel3Folders] = useState([]);

  const [selectedLevel1, setSelectedLevel1] = useState("");
  const [selectedLevel2, setSelectedLevel2] = useState("");
  const [selectedLevel3, setSelectedLevel3] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const project = JSON.parse(localStorage.getItem("selectedProject") || "null");
  const projectId = project ? project.id : null;

  const showModuleSelection = operation === "copy" && file?.module === "plan";

  const getAvailableModules = useCallback(() => {
    if (file?.module === "plan" && operation === "copy") {
      return [
        { id: "marketing", name: "Marketing" },
        { id: "construction", name: "Construction" }
      ];
    }
    return [];
  }, [file?.module, operation]);

  useEffect(() => {
    if (isOpen) {
      if (operation === "copy" && file?.module === "plan") {
        setSelectedModule("marketing");
      } else {
        setSelectedModule(file?.module || "");
      }

      // 重置所有选择
      setSelectedLevel1("");
      setSelectedLevel2("");
      setSelectedLevel3("");
      setLevel1Folders([]);
      setLevel2Folders([]);
      setLevel3Folders([]);
      setError("");
    }
  }, [isOpen, operation, file?.module]);

  // 获取一级文件夹
  const fetchLevel1Folders = useCallback(async () => {
    if (!selectedModule || !projectId) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        module: selectedModule,
        projectId: projectId,
        view_mode: "flat"
      });

      // 正确处理parentId参数
      params.append("parent_id", "null");

      const response = await fetch(`/api/folders?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load folders: ${response.statusText}`);
      }
      const data = await response.json();
      const folders = Array.isArray(data) ? data : (data.folders || []);

      setLevel1Folders(folders);

      // 重置下级选择
      setSelectedLevel2("");
      setSelectedLevel3("");
      setLevel2Folders([]);
      setLevel3Folders([]);
    } catch (err) {
      console.error("Error fetching level 1 folders:", err);
      setError(err.message || "Failed to load level 1 folders");
      setLevel1Folders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedModule, projectId]);

  // 获取二级文件夹
  const fetchLevel2Folders = useCallback(async (parentId) => {
    if (!selectedModule || !projectId || !parentId) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        module: selectedModule,
        projectId: projectId,
        view_mode: "flat"
      });

      // 正确处理parentId参数
      if (parentId && parentId !== "null") {
        params.append("parent_id", parentId);
      } else {
        params.append("parent_id", "null");
      }
      const response = await fetch(`/api/folders?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load folders: ${response.statusText}`);
      }

      const data = await response.json();
      const folders = Array.isArray(data) ? data : (data.folders || []);
      setLevel2Folders(folders);

      // 重置三级选择
      setSelectedLevel3("");
      setLevel3Folders([]);
    } catch (err) {
      console.error("Error fetching level 2 folders:", err);
      setError(err.message || "Failed to load level 2 folders");
      setLevel2Folders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedModule, projectId]);

  // 获取三级文件夹
  const fetchLevel3Folders = useCallback(async (parentId) => {
    if (!selectedModule || !projectId || !parentId) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({
        module: selectedModule,
        projectId: projectId,
        view_mode: "flat"
      });

      // 正确处理parentId参数
      if (parentId && parentId !== "null") {
        params.append("parent_id", parentId);
      } else {
        params.append("parent_id", "null");
      }
      const response = await fetch(`/api/folders?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load folders: ${response.statusText}`);
      }

      const data = await response.json();
      const folders = Array.isArray(data) ? data : (data.folders || []);
      setLevel3Folders(folders);
    } catch (err) {
      console.error("Error fetching level 3 folders:", err);
      setError(err.message || "Failed to load level 3 folders");
      setLevel3Folders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedModule, projectId]);

  // 当选择模块时，获取一级文件夹
  useEffect(() => {
    if (isOpen && selectedModule) {
      fetchLevel1Folders();
    }
  }, [isOpen, selectedModule, fetchLevel1Folders]);

  // 当选择一级文件夹时，获取二级文件夹
  useEffect(() => {
    if (selectedLevel1) {
      fetchLevel2Folders(selectedLevel1);
    } else {
      setLevel2Folders([]);
      setSelectedLevel2("");
      setLevel3Folders([]);
      setSelectedLevel3("");
    }
  }, [selectedLevel1, fetchLevel2Folders]);

  // 当选择二级文件夹时，获取三级文件夹
  useEffect(() => {
    if (selectedLevel2) {
      fetchLevel3Folders(selectedLevel2);
    } else {
      setLevel3Folders([]);
      setSelectedLevel3("");
    }
  }, [selectedLevel2, fetchLevel3Folders]);

  // 获取最终选择的文件夹ID
  const getFinalFolderId = () => {
    // 优先选择最深层级的文件夹
    if (selectedLevel3) return selectedLevel3;
    if (selectedLevel2) return selectedLevel2;
    if (selectedLevel1) return selectedLevel1;
    return "";
  };

  // 获取选择路径的显示文本
  const getSelectedPath = () => {
    const parts = [];

    if (selectedLevel1) {
      const level1Folder = level1Folders.find(f => f._id === selectedLevel1);
      if (level1Folder) parts.push(level1Folder.name);
    }

    if (selectedLevel2) {
      const level2Folder = level2Folders.find(f => f._id === selectedLevel2);
      if (level2Folder) parts.push(level2Folder.name);
    }

    if (selectedLevel3) {
      const level3Folder = level3Folders.find(f => f._id === selectedLevel3);
      if (level3Folder) parts.push(level3Folder.name);
    }

    return parts.length > 0 ? parts.join(" > ") : "No selection";
  };

  const handleSubmit = async () => {
    const finalFolderId = getFinalFolderId();
    if (!finalFolderId) {
      setError("Please select a destination folder");
      return;
    }

    try {
      setLoading(true);
      const endpoint = operation === "move" 
        ? `/api/file/${file.module}/${file._id}/move`
        : `/api/file/${file.module}/${file._id}/copy`;

      const response = await fetch(endpoint, {
        method: operation === "move" ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          target_module: selectedModule,
          target_folder_id: finalFolderId,
          project_id: projectId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Operation failed");
      }

      onConfirm();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-[500px] max-h-[600px] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {operation === "move" ? "Move to..." : "Copy to..."}
        </h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {showModuleSelection && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Module
            </label>
            <select
              value={selectedModule}
              onChange={(e) => setSelectedModule(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={loading}
            >
              {getAvailableModules().map((module) => (
                <option key={module.id} value={module.id}>
                  {module.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 当前选择路径显示 */}
        <div className="mb-4 p-3 bg-gray-50 rounded">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Selected Path:
          </label>
          <div className="text-sm text-blue-600 font-medium">
            {getSelectedPath()}
          </div>
        </div>

        {/* 一级文件夹选择 */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Step 1: Select Level 1 Folder
          </label>
          <select
            value={selectedLevel1}
            onChange={(e) => setSelectedLevel1(e.target.value)}
            className="w-full p-2 border rounded"
            disabled={loading}
          >
            <option value="">Select a level 1 folder</option>
            {level1Folders.map((folder) => (
              <option key={folder._id} value={folder._id}>
                {folder.name} ({folder.fileCount || 0} files, {folder.subfolderCount || 0} subfolders)
              </option>
            ))}
          </select>
        </div>

        {/* 二级文件夹选择 */}
        {selectedLevel1 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Step 2: Select Level 2 Folder (Optional)
            </label>
            <select
              value={selectedLevel2}
              onChange={(e) => setSelectedLevel2(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={loading}
            >
              <option value="">Stop at Level 1 or select Level 2 folder</option>
              {level2Folders.map((folder) => (
                <option key={folder._id} value={folder._id}>
                  {folder.name} ({folder.fileCount || 0} files, {folder.subfolderCount || 0} subfolders)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 三级文件夹选择 */}
        {selectedLevel2 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Step 3: Select Level 3 Folder (Optional)
            </label>
            <select
              value={selectedLevel3}
              onChange={(e) => setSelectedLevel3(e.target.value)}
              className="w-full p-2 border rounded"
              disabled={loading}
            >
              <option value="">Stop at Level 2 or select Level 3 folder</option>
              {level3Folders.map((folder) => (
                <option key={folder._id} value={folder._id}>
                  {folder.name} ({folder.fileCount || 0} files)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            disabled={loading || !getFinalFolderId()}
          >
            {loading ? "Processing..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileOperationModal;